import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { insights, businesses } from '../mockData';

const InsightDetailPage = () => {
  const { slug } = useParams();
  const post = insights.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-alabaster">
        <div className="text-center">
          <div className="eyebrow mb-4">404</div>
          <h1 className="font-display text-5xl text-brand-oxford mb-6">Insight not found</h1>
          <Link to="/insights" className="text-brand-oxford border-b border-brand-oxford pb-1">Back to insights</Link>
        </div>
      </div>
    );
  }

  const business = businesses.find((b) => b.id === post.business);
  const accent = `rgb(var(--sub-${business?.subBrand || 'crm'}))`;

  const related = insights.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { '@type': 'Person', name: post.author.name, jobTitle: post.author.role },
    articleSection: post.category,
    wordCount: post.content.reduce((n, blk) => n + (blk.text ? blk.text.split(/\s+/).length : (blk.items || []).join(' ').split(/\s+/).length), 0),
    image: `https://lockene.info${post.heroImage}`,
    publisher: { '@type': 'Organization', name: 'Lockene' }
  };

  const renderBlock = (blk, i) => {
    if (blk.type === 'h') {
      return (
        <h2 key={i} className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford mt-14 mb-4">
          {blk.text}
        </h2>
      );
    }
    if (blk.type === 'l') {
      return (
        <ul key={i} className="list-none space-y-3 my-6">
          {blk.items.map((it, j) => (
            <li key={j} className="flex items-start gap-3 text-lg text-brand-slate leading-relaxed">
              <span aria-hidden className="mt-3 w-2 h-2 shrink-0" style={{ backgroundColor: accent }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="text-lg text-brand-slate leading-relaxed my-5">
        {blk.text}
      </p>
    );
  };

  return (
    <>
      <Seo
        title={`${post.title} · Lockene Insights`}
        description={post.excerpt}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-14" aria-labelledby="insight-heading" data-testid={`insight-hero-${post.slug}`}>
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-12" />
          <div className="max-w-4xl">
            <Link
              to="/insights"
              data-testid="insight-back"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate hover:text-brand-oxford transition-colors mb-8"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> All Insights
            </Link>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-10 h-px" style={{ backgroundColor: accent }} aria-hidden />
              <span className="text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: accent }}>
                {post.category} · {post.readMinutes} min read · <time dateTime={post.date}>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</time>
              </span>
            </div>
            <h1 id="insight-heading" className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-brand-oxford">
              {post.title}
            </h1>
            <div className="mt-10 flex items-center gap-4">
              <div
                aria-hidden
                className="w-12 h-12 border border-brand-oxford flex items-center justify-center font-display text-lg text-brand-oxford"
              >
                {post.author.name.split(/\s+/).map((n) => n[0]).slice(0, 2).join('')}
              </div>
              <div>
                <div className="text-sm font-semibold text-brand-oxford">{post.author.name}</div>
                <div className="text-xs text-brand-slate">{post.author.role}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="bg-brand-alabaster">
        <div className="container-editorial pb-14">
          <div className="relative">
            <img src={post.heroImage} alt={post.title} className="w-full max-h-[540px] object-cover border border-brand-hairline" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="section bg-white border-t border-brand-hairline pt-16 md:pt-24" aria-label="Insight body">
        <div className="container-editorial max-w-3xl">
          <article className="prose max-w-none">
            <Reveal>
              <p className="font-display text-2xl md:text-3xl leading-[1.3] text-brand-oxford mb-10">
                {post.excerpt}
              </p>
            </Reveal>
            {post.content.map((blk, i) => renderBlock(blk, i))}
          </article>

          <div className="mt-16 pt-8 border-t border-brand-hairline flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">Author</div>
              <div className="font-display text-2xl text-brand-oxford mt-1">{post.author.name}</div>
              <div className="text-sm text-brand-slate">{post.author.role}</div>
            </div>
            {business && (
              <Link
                to={`/business/${business.id}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1"
              >
                Visit {business.name} <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="section bg-brand-alabaster border-t border-brand-hairline" aria-labelledby="related-heading">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Continue Reading</span>
              </div>
              <h2 id="related-heading" className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford">
                More from Lockene.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 60}>
                  <Link
                    to={`/insights/${p.slug}`}
                    className="group bg-white border border-brand-hairline hover:shadow-lift transition-shadow flex flex-col h-full"
                  >
                    <img src={p.heroImage} alt={p.title} className="w-full h-44 object-cover grayscale group-hover:grayscale-0 transition-all duration-700" loading="lazy" />
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">{p.category} · {p.readMinutes} min</div>
                      <h3 className="font-display text-xl leading-tight text-brand-oxford mt-3">{p.title}</h3>
                      <p className="mt-3 text-sm text-brand-slate leading-relaxed flex-1">{p.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default InsightDetailPage;
